import { useState } from "react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { validateReimbursementDate } from "./expenseReportRules";
import { todayIsoDate } from "./expenseScreenRules";
import type { Expense } from "./expenseTypes";
import { useSetReimbursed } from "./useSetReimbursed";

/**
 * "Marcar como reembolsado": asks for the reimbursement date (default today,
 * never in the future) and applies it to every given expense.
 */
export const ExpenseReimburseDialog = ({
  expenses,
  open,
  onClose,
}: {
  expenses: Expense[];
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const today = todayIsoDate();
  const [date, setDate] = useState(today);
  const [error, setError] = useState<string | undefined>();
  const close = () => {
    setDate(today);
    setError(undefined);
    onClose();
  };
  const { mutate, isPending } = useSetReimbursed(close);

  const handleConfirm = () => {
    const message = validateReimbursementDate(date, todayIsoDate());
    if (message) {
      setError(message);
      return;
    }
    mutate({ expenses, reimbursedAt: date });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.expenses.reimbursement.dialog_title", {
              smart_count: expenses.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.expenses.reimbursement.dialog_description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="expense-reimbursed-at">
            {translate("crm.expenses.reimbursement.date")}
          </Label>
          <input
            id="expense-reimbursed-at"
            type="date"
            max={today}
            value={date}
            disabled={isPending}
            aria-invalid={error != null}
            className="h-11 rounded-md border bg-background px-3 text-base"
            onChange={(event) => {
              setDate(event.target.value);
              setError(undefined);
            }}
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            disabled={isPending}
            onClick={close}
          >
            {translate("crm.expenses.cancel.back")}
          </Button>
          <Button
            type="button"
            className="min-h-11"
            disabled={isPending}
            onClick={handleConfirm}
          >
            {translate("crm.expenses.reimbursement.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The reimbursement action of one expense: "Marcar como reembolsado" while
 * pending, "Deshacer reembolso" once reimbursed. Renders nothing for an
 * expense that cannot be reimbursed.
 */
export const ExpenseReimbursementAction = ({
  expense,
}: {
  expense: Expense;
}) => {
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate, isPending } = useSetReimbursed();
  if (expense.paid_by !== "pepe_diaz" || expense.cancelled_at) return null;
  if (expense.reimbursed) {
    return (
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={isPending}
        onClick={() => mutate({ expenses: [expense], reimbursedAt: null })}
      >
        {translate("crm.expenses.reimbursement.unmark")}
      </Button>
    );
  }
  return (
    <>
      <Button
        type="button"
        className="min-h-11"
        onClick={() => setIsOpen(true)}
      >
        {translate("crm.expenses.reimbursement.mark")}
      </Button>
      <ExpenseReimburseDialog
        expenses={[expense]}
        open={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
